/**
 * Inbox Dashboard page — analytics and metrics for business conversations.
 *
 * Shows summary stats, per-persona breakdown, and activity charts
 * for conversations across all channels.
 */

import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/utils'
import {
  BarChart3,
  MessageSquare,
  Users,
  AlertTriangle,
  Phone,
  Globe,
  RefreshCw,
} from 'lucide-react'
import { Card, Button, Badge, Spinner } from '@/components/ui'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useInbox } from '@/hooks/useInbox'
import { useFeature } from '@/hooks/useFeature'
import { useOrders } from '@/hooks/useOrders'
import { QUEUE_STATE_CONFIG, WAITING_ON_LABELS } from '@/constants/queueStates'
import type { InboxAnalytics } from '@/types/inbox'

function StatCard({
  label,
  value,
  icon: Icon,
  color = 'text-primary',
}: {
  label: string
  value: number
  icon: typeof MessageSquare
  color?: string
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className={cn('p-2 rounded-lg bg-muted', color)}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">
            {value}
          </p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
    </Card>
  )
}

/** Simple horizontal bar chart row for activity data */
function ActivityBar({
  label,
  value,
  maxValue,
  color,
}: {
  label: string
  value: number
  maxValue: number
  color: string
}) {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0

  return (
    <div className="flex items-center gap-3">
      <span className="w-20 text-xs text-muted-foreground text-right shrink-0">
        {label}
      </span>
      <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', color)}
          style={{ width: `${Math.max(pct, value > 0 ? 2 : 0)}%` }}
        />
      </div>
      <span className="w-8 text-xs text-muted-foreground text-right shrink-0">
        {value}
      </span>
    </div>
  )
}

export function InboxDashboardPage() {
  const navigate = useNavigate()
  const { fetchAnalytics } = useInbox()
  const [analytics, setAnalytics] = useState<InboxAnalytics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [days, setDays] = useState(7)

  // SA-285: queue counters — this page is route-gated on `inbox` only, so
  // the orders stats endpoint needs its own in-page feature guard. Since the
  // E4 commerce cut (SA-384/SA-389, spec D26) that router rides
  // `workflow_engine`, not `commerce`.
  const recordsEnabled = useFeature('workflow_engine')
  const { fetchStats, stats: orderStats } = useOrders()
  useEffect(() => {
    if (recordsEnabled) fetchStats()
  }, [recordsEnabled, fetchStats])
  const queue = orderStats?.queue ?? null

  const loadAnalytics = useCallback(async () => {
    setIsLoading(true)
    const data = await fetchAnalytics(days)
    setAnalytics(data)
    setIsLoading(false)
  }, [fetchAnalytics, days])

  useEffect(() => {
    loadAnalytics()
  }, [loadAnalytics])

  // Aggregate activity data by date for the bar chart
  const dailyTotals: { date: string; web: number; whatsapp: number }[] = []
  if (analytics?.activity) {
    const byDate: Record<string, { web: number; whatsapp: number }> = {}
    for (const a of analytics.activity) {
      if (!byDate[a.date]) byDate[a.date] = { web: 0, whatsapp: 0 }
      if (a.channel === 'whatsapp') byDate[a.date].whatsapp += a.messageCount
      else byDate[a.date].web += a.messageCount
    }
    for (const [date, counts] of Object.entries(byDate).sort()) {
      dailyTotals.push({ date, ...counts })
    }
  }
  const maxMessages = Math.max(
    ...dailyTotals.map((d) => d.web + d.whatsapp),
    1
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  return (
    <PageFrame width="detail" className="space-y-6">
      <PageHeader
        title="Inbox Dashboard"
        icon={BarChart3}
        back={{ to: '/inbox', label: 'Inbox' }}
        actions={
          <>
            {([7, 14, 30] as const).map((d) => (
              <Button
                key={d}
                variant={days === d ? 'default' : 'secondary'}
                size="sm"
                className="text-xs"
                onClick={() => setDays(d)}
              >
                {d}d
              </Button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              aria-label="Refresh"
              onClick={loadAnalytics}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </>
        }
      />

      {analytics && (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Conversations today"
              value={analytics.conversationsToday}
              icon={MessageSquare}
              color="text-primary"
            />
            <StatCard
              label="Messages today"
              value={analytics.messagesToday}
              icon={MessageSquare}
              color="text-info"
            />
            <StatCard
              label="Active"
              value={analytics.activeConversations}
              icon={Users}
              color="text-success"
            />
            <StatCard
              label="Escalated"
              value={analytics.humanConversations}
              icon={AlertTriangle}
              color="text-destructive"
            />
          </div>

          {recordsEnabled && queue && queue.total > 0 && (
            <Card className="p-4 gap-0">
              <h2 className="text-sm font-medium text-foreground mb-3">
                Order queue
              </h2>
              <div className="flex items-center gap-6 flex-wrap">
                <div>
                  <p className="text-2xl font-bold text-foreground">{queue.total}</p>
                  <p className="text-xs text-muted-foreground">Open orders</p>
                </div>
                {Object.entries(QUEUE_STATE_CONFIG).map(([state, config]) => {
                  const count = queue.byState[state] ?? 0
                  if (!count) return null
                  return (
                    <div key={state} className="flex items-center gap-2">
                      <Badge variant="secondary" className={cn('text-xs', config.className)}>
                        {config.label}
                      </Badge>
                      <span className="text-sm font-medium text-foreground">{count}</span>
                    </div>
                  )
                })}
              </div>
              {(() => {
                const waiting = Object.entries(WAITING_ON_LABELS)
                  .map(([owner, label]) => [label, queue.byWaitingOn[owner] ?? 0] as const)
                  .filter(([, count]) => count > 0)
                if (waiting.length === 0) return null
                return (
                  <p className="text-xs text-muted-foreground mt-2">
                    Waiting on: {waiting.map(([label, count]) => `${label} (${count})`).join(', ')}
                  </p>
                )
              })()}
            </Card>
          )}

          {/* Channel breakdown */}
          <Card className="p-4 gap-0">
            <h2 className="text-sm font-medium text-foreground mb-3">
              Channel Breakdown
            </h2>
            <div className="flex items-center gap-6">
              {Object.entries(analytics.channelBreakdown).map(([ch, count]) => (
                <div key={ch} className="flex items-center gap-2">
                  {ch === 'whatsapp' ? (
                    <Phone className="w-4 h-4 text-green-500" />
                  ) : (
                    <Globe className="w-4 h-4 text-blue-500" />
                  )}
                  <span className="text-sm font-medium text-foreground capitalize">
                    {ch}
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {count}
                  </Badge>
                </div>
              ))}
              {Object.keys(analytics.channelBreakdown).length === 0 && (
                <span className="text-sm text-muted-foreground">No conversations yet</span>
              )}
            </div>
          </Card>

          {/* Per-persona breakdown */}
          {analytics.personaBreakdown.length > 0 && (
            <Card className="p-4 gap-0">
              <h2 className="text-sm font-medium text-foreground mb-3">
                Per-Persona Breakdown
              </h2>
              <div className="space-y-3">
                {analytics.personaBreakdown.map((p) => (
                  <div
                    key={p.personaId}
                    className="flex items-center justify-between py-2 border-b border-border last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-sm text-foreground">
                        {p.personaName}
                      </span>
                      <div className="flex items-center gap-1">
                        {Object.entries(p.channels).map(([ch, cnt]) => (
                          <Badge key={ch} variant="secondary" className="text-[10px]">
                            {ch === 'whatsapp' ? (
                              <Phone className="w-2.5 h-2.5 mr-0.5 inline" />
                            ) : (
                              <Globe className="w-2.5 h-2.5 mr-0.5 inline" />
                            )}
                            {cnt}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{p.conversationCount} convos</span>
                      <span>{p.messageCount} msgs</span>
                      {p.unreadCount > 0 && (
                        <Badge variant="default" className="text-[10px]">
                          {p.unreadCount} unread
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs h-6"
                        onClick={() => navigate(`/inbox?persona=${p.personaId}`)}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Activity chart (simple bar chart) */}
          <Card className="p-4 gap-0">
            <h2 className="text-sm font-medium text-foreground mb-1">
              Message Activity (last {days} days)
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              Messages per day by channel
            </p>

            {dailyTotals.length > 0 ? (
              <div className="space-y-2">
                {dailyTotals.map((d) => {
                  const dateLabel = new Date(d.date + 'T12:00:00Z').toLocaleDateString([], {
                    month: 'short',
                    day: 'numeric',
                  })
                  return (
                    <div key={d.date} className="space-y-1">
                      <ActivityBar
                        label={dateLabel}
                        value={d.web + d.whatsapp}
                        maxValue={maxMessages}
                        color="bg-primary"
                      />
                      {d.whatsapp > 0 && (
                        <div className="ml-[92px]">
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                            <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                            WhatsApp: {d.whatsapp}
                            <span className="inline-block w-2 h-2 rounded-full bg-blue-500 ml-2" />
                            Web: {d.web}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No activity data available
              </p>
            )}
          </Card>

          {/* Total stats footer */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{analytics.totalConversations} total conversations</span>
            <span>{analytics.totalUnread} unread messages</span>
          </div>
        </>
      )}

      {!analytics && !isLoading && (
        <div className="text-center py-12">
          <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No analytics data available</p>
        </div>
      )}
    </PageFrame>
  )
}
